import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldError,
    FieldGroup,
    FieldLabel,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Text,
} from "@athena/ui";
import type { Role } from "@athena/common/graphql/generated/schema";
import { CreateUserDocument, SendUserInviteDocument } from "@/graphql/users.generated";
import { BrandsDocument } from "@/graphql/brands.generated";
import { roleLabelMap } from "@/types/Roles";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";
import { useFeatureFlag } from "@athena/config/flags";

/**
 * Creates a person by hand and emails them an invitation.
 *
 * There is no password to set and nothing to accept: Auth0 owns credentials, and the server adopts
 * a hand-created row on that person's first sign-in by matching their email. So the invitation says
 * "sign in with this address", and the account — org, role and brand already set — is waiting.
 *
 * The two mutations are deliberately separate. A mail failure must not read as a failed creation:
 * the person exists either way, and an admin told "creation failed" will create them twice. So the
 * dialog reports the halves independently, and the same invite call is the resend.
 *
 * Fields mirror the user detail editor rather than inventing a second vocabulary — same org picker,
 * same role and brand lists — because this is the same record, created instead of edited.
 */

/**
 * What a platform admin may grant. ADMIN and SYSTEM are platform-wide, and the server refuses them
 * from anyone who is not already one — see `createUser`, which gates on that rather than trusting
 * this list. SYSTEM is omitted entirely: it is for service accounts, not people you email.
 */
const PLATFORM_ASSIGNABLE_ROLES: Role[] = [
    "USER",
    "COACH",
    "ORG_ADMIN",
    "SUB_ORG_ADMIN",
    "ADMIN",
];

/** What an org admin may grant inside their own organization. No platform roles. */
const ORG_ASSIGNABLE_ROLES: Role[] = [
    "USER",
    "COACH",
    "ORG_ADMIN",
    "SUB_ORG_ADMIN",
];

export const NO_ORG = "__none__";

/** Mirrors `isValidEmail` server-side. Client-side is a courtesy; the resolver is the enforcement. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InviteValues {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    organizationId: string;
    role: Role;
    brand: string;
    dateOfBirth: string;
}

const EMPTY: InviteValues = {
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    organizationId: NO_ORG,
    role: "USER",
    brand: "RELEASE_U",
    dateOfBirth: "",
};

/**
 * Field-level messages, keyed by field so each one renders under its own input. A single form-level
 * error would make an admin hunt for which of eight fields it meant.
 *
 * Exported and unit-tested directly rather than driven through the rendered form. A Radix `Select`
 * inside a Radix `Dialog` sends jsdom into an unbounded focus loop — the select's portal takes
 * focus, the dialog's focus scope takes it back — which overflows the stack rather than failing a
 * test. The cross-field rules are pure, so testing them here costs nothing and tests our logic
 * instead of Radix's focus management.
 */
export const validateInvite = (
    values: InviteValues,
    /** True for a caller who cannot create a user outside an organization — anyone but a platform admin. */
    requireOrganization = false,
): Partial<Record<keyof InviteValues, string>> => {
    const errors: Partial<Record<keyof InviteValues, string>> = {};

    const email = values.email.trim();
    if (!email) errors.email = "An email address is required — it is how they sign in.";
    else if (!EMAIL_RE.test(email)) errors.email = "That does not look like an email address.";

    // Optional, but a blank-but-typed value (spaces) is a mistake worth catching.
    if (values.phoneNumber.trim() && values.phoneNumber.replace(/\D/g, "").length < 10) {
        errors.phoneNumber = "Enter a full phone number, including area code.";
    }

    const dob = values.dateOfBirth.trim();
    if (dob) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
            errors.dateOfBirth = "Use YYYY-MM-DD.";
        } else {
            const parsed = new Date(`${dob}T00:00:00Z`);
            if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== dob) {
                errors.dateOfBirth = "That is not a real date.";
            } else if (parsed.getTime() > Date.now()) {
                errors.dateOfBirth = "A birth date cannot be in the future.";
            }
        }
    }

    // A role that only means something inside an organization needs one named.
    if (values.organizationId === NO_ORG && (values.role === "ORG_ADMIN" || values.role === "SUB_ORG_ADMIN")) {
        errors.organizationId = `${roleLabelMap[values.role]} administers an organization — pick which one.`;
    }

    /**
     * An account belonging to no organization is a platform account, and `createUser` is
     * platform-only for that case. Caught here so an org admin reads it as a required field rather
     * than as a permission error after submitting.
     */
    if (values.organizationId === NO_ORG && requireOrganization) {
        errors.organizationId = "Pick the organization this person joins.";
    }

    return errors;
};

export function InviteUserDialog({
    open,
    onOpenChange,
    onCreated,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Fired after the account exists, whether or not the invitation reached them. */
    onCreated: () => void;
}) {
    const { t } = useTranslation();
    const [
        values,
        setValues,
    ] = useState<InviteValues>(EMPTY);
    /** Populated on submit, not per keystroke — nobody wants "invalid email" while typing one. */
    const [
        errors,
        setErrors,
    ] = useState<Partial<Record<keyof InviteValues, string>>>({});
    const [
        sendInvite,
        setSendInvite,
    ] = useState<boolean>(true);

    const { isPlatformAdmin, ownOrganizationId } = usePlatformAdmin();
    const ageRatingEnabled = useFeatureFlag("birthdateEnabled");
    const { data: brandsData } = useQuery(BrandsDocument);
    const [
        createUser,
        { loading: creating },
    ] = useMutation(CreateUserDocument);
    const [sendUserInvite] = useMutation(SendUserInviteDocument);

    const assignableRoles = isPlatformAdmin ? PLATFORM_ASSIGNABLE_ROLES : ORG_ASSIGNABLE_ROLES;

    /**
     * An org admin belongs to exactly one organization and every user they create joins it, so the
     * picker starts there rather than at "No organization" — a default they would have to correct
     * every time and which the server would refuse.
     */
    useEffect(() => {
        if (!isPlatformAdmin && ownOrganizationId) {
            setValues((prev) =>
                prev.organizationId === NO_ORG ? { ...prev, organizationId: ownOrganizationId } : prev,
            );
        }
    }, [
        isPlatformAdmin,
        ownOrganizationId,
    ]);
    const set = <K extends keyof InviteValues>(key: K, value: InviteValues[K]) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        // Clear this field's message as soon as it is edited, so a corrected field stops shouting.
        setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    };

    const close = () => {
        onOpenChange(false);
        setValues(isPlatformAdmin || !ownOrganizationId ? EMPTY : { ...EMPTY, organizationId: ownOrganizationId });
        setErrors({});
        setSendInvite(true);
    };

    const submit = async () => {
        // A hidden field cannot be corrected, so it must not be able to fail validation either.
        const found = validateInvite(ageRatingEnabled ? values : { ...values, dateOfBirth: "" }, !isPlatformAdmin);
        setErrors(found);
        if (Object.keys(found).length > 0) return;

        let created;
        try {
            const { data } = await createUser({
                variables: {
                    input: {
                        email: values.email.trim(),
                        firstName: values.firstName.trim() || undefined,
                        lastName: values.lastName.trim() || undefined,
                        phoneNumber: values.phoneNumber.trim() || undefined,
                        organizationId: values.organizationId === NO_ORG ? undefined : values.organizationId,
                        role: values.role,
                        brand: values.brand,
                        // Never sent while the feature is off — the resolver refuses it outright.
                        dateOfBirth: ageRatingEnabled ? values.dateOfBirth.trim() || undefined : undefined,
                    },
                },
            });
            created = data?.createUser;
        } catch (error) {
            Sentry.captureException(error);
            // The server names the reason — an address already taken, an unreachable org — and it
            // is more useful than "could not create user".
            toast.error((error as Error).message || t("errors.failedToSave", { variable: "user" }));
            return;
        }

        if (!created) return;
        onCreated();

        if (!sendInvite) {
            toast.success(`${created.email} added. No invitation sent.`);
            close();
            return;
        }

        /**
         * Past the point of no return for the account: it exists. So a mail failure is reported as
         * its own outcome rather than as a failure of the whole action, and the dialog still closes
         * — leaving it open would suggest retrying the create, which would now conflict.
         */
        try {
            const { data } = await sendUserInvite({ variables: { userId: created.id } });
            if (data?.sendUserInvite) toast.success(`${created.email} invited.`);
            else toast.warning(`${created.email} was added, but the invitation could not be sent. Try resending.`);
        } catch (error) {
            Sentry.captureException(error);
            toast.warning(`${created.email} was added, but the invitation could not be sent. Try resending.`);
        }
        close();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Invite a user</DialogTitle>
                    <DialogDescription>
                        Creates the account now and emails them to sign in. There is no password to set — they use this
                        address, and everything below is already applied when they arrive.
                    </DialogDescription>
                </DialogHeader>
                <FieldGroup>
                    <Field>
                        <FieldLabel htmlFor="invite-email" required>
                            Email
                        </FieldLabel>
                        <Input
                            id="invite-email"
                            type="email"
                            autoComplete="off"
                            placeholder="person@example.com"
                            aria-invalid={!!errors.email}
                            value={values.email}
                            onChange={(e) => set("email", e.target.value)}
                        />
                        <FieldError>{errors.email}</FieldError>
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field>
                            <FieldLabel htmlFor="invite-first" optional>
                                First name
                            </FieldLabel>
                            <Input
                                id="invite-first"
                                type="text"
                                value={values.firstName}
                                onChange={(e) => set("firstName", e.target.value)}
                            />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="invite-last" optional>
                                Last name
                            </FieldLabel>
                            <Input
                                id="invite-last"
                                type="text"
                                value={values.lastName}
                                onChange={(e) => set("lastName", e.target.value)}
                            />
                        </Field>
                    </div>
                    <Field>
                        <FieldLabel htmlFor="invite-phone" optional>
                            Phone number
                        </FieldLabel>
                        <Input
                            id="invite-phone"
                            type="tel"
                            placeholder="+1 555 123 4567"
                            aria-invalid={!!errors.phoneNumber}
                            value={values.phoneNumber}
                            onChange={(e) => set("phoneNumber", e.target.value)}
                        />
                        <FieldError>{errors.phoneNumber}</FieldError>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="invite-org" required={!isPlatformAdmin} optional={isPlatformAdmin}>
                            Organization
                        </FieldLabel>
                        <OrganizationSelect
                            id="invite-org"
                            aria-label="Organization"
                            aria-invalid={!!errors.organizationId}
                            value={values.organizationId}
                            onValueChange={(v) => set("organizationId", v)}
                            /* "No organization" is offered only to a platform admin: for anyone
                               else it is a choice the server refuses. */
                            leadingOptions={isPlatformAdmin ? [{ value: NO_ORG, label: "No organization" }] : []}
                        />
                        <FieldError>{errors.organizationId}</FieldError>
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field>
                            <FieldLabel htmlFor="invite-role">Role</FieldLabel>
                            <Select value={values.role} onValueChange={(v) => set("role", v as Role)}>
                                <SelectTrigger id="invite-role" aria-label="Role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {assignableRoles.map((role) => (
                                        <SelectItem key={role} value={role}>
                                            {roleLabelMap[role]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="invite-brand">Brand</FieldLabel>
                            <Select value={values.brand} onValueChange={(v) => set("brand", v)}>
                                <SelectTrigger id="invite-brand" aria-label="Brand">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {(brandsData?.brands ?? []).map((brand) => (
                                        <SelectItem key={brand.slug} value={brand.slug}>
                                            {brand.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    {/*
                     * Only while the age feature is on. The backend refuses to store a date of birth
                     * with `birthdateEnabled` off, so offering the field would collect something that
                     * cannot be saved — and the flag exists precisely so that no age is collected.
                     */}
                    {ageRatingEnabled && (
                        <Field>
                            <FieldLabel htmlFor="invite-dob" optional>
                                Date of birth
                            </FieldLabel>
                            <Input
                                id="invite-dob"
                                type="date"
                                aria-invalid={!!errors.dateOfBirth}
                                value={values.dateOfBirth}
                                onChange={(e) => set("dateOfBirth", e.target.value)}
                            />
                            {/* Says what it is for, because "optional" alone hides a real consequence. */}
                            <Text muted className="text-xs">
                                Without one, age-restricted courses stay locked for them until they add it themselves.
                            </Text>
                            <FieldError>{errors.dateOfBirth}</FieldError>
                        </Field>
                    )}

                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <FieldLabel htmlFor="invite-send">Email the invitation</FieldLabel>
                            <Text muted className="text-xs">
                                Off creates the account silently — they can still sign in with this address.
                            </Text>
                        </div>
                        <Switch
                            id="invite-send"
                            aria-label="Email the invitation"
                            checked={sendInvite}
                            onCheckedChange={setSendInvite}
                        />
                    </div>
                </FieldGroup>
                <DialogFooter>
                    <Button variant="outline" className="w-auto" disabled={creating} onClick={close}>
                        {t("common.actions.cancel")}
                    </Button>
                    {/* Repeats the action rather than saying Save, per `.claude/rules/ui.md`. */}
                    <Button className="w-auto" disabled={creating} onClick={submit}>
                        {creating ? "Creating…" : sendInvite ? "Create and invite" : "Create account"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
